"use client"

/**
 * How staff attach files. On a phone — the owner's pick "B" (2026-10-18) — two buttons: Camera opens the
 * camera at once (the usual before/after shot), Gallery picks photos and videos already taken. On a
 * computer one button; dropping files or pasting them into the text works too (the blocks that use this).
 */
import * as React from "react"
import { Camera, Images, Paperclip } from "lucide-react"
import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/button"
import { useIsMobile } from "@/components/ui/use-mobile"
import { cn } from "@/lib/utils"

const ACCEPT = "image/*,video/*"

function useTouchDevice(): boolean {
  const isMobile = useIsMobile()
  const [coarse, setCoarse] = React.useState(false)
  React.useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return
    const mq = window.matchMedia("(pointer: coarse)")
    setCoarse(mq.matches)
    const on = () => setCoarse(mq.matches)
    mq.addEventListener?.("change", on)
    return () => mq.removeEventListener?.("change", on)
  }, [])
  return !!isMobile || coarse
}

function Picker({ children, capture, multiple, disabled, onFiles, title }: {
  children: React.ReactNode; capture?: "environment"; multiple: boolean; disabled?: boolean
  onFiles: (files: FileList) => void; title?: string
}) {
  const ref = React.useRef<HTMLInputElement>(null)
  return (
    <>
      <Button type="button" variant="outline" size="sm" disabled={disabled} title={title}
              className="h-8 gap-1.5 px-2.5 text-xs" onClick={() => ref.current?.click()}>
        {children}
      </Button>
      <input ref={ref} type="file" hidden accept={ACCEPT} multiple={multiple} {...(capture ? { capture } : {})}
             onChange={(e) => { if (e.target.files?.length) onFiles(e.target.files); e.target.value = "" }} />
    </>
  )
}

export function AttachControls({ onFiles, disabled, className }: {
  onFiles: (files: FileList) => void; disabled?: boolean; className?: string
}) {
  const { t } = useTranslation()
  const touch = useTouchDevice()
  if (touch) {
    return (
      <div className={cn("flex gap-2", className)}>
        <Picker capture="environment" multiple={false} disabled={disabled} onFiles={onFiles}>
          <Camera className="h-4 w-4" />{t("attachments.camera", "Camera")}
        </Picker>
        <Picker multiple disabled={disabled} onFiles={onFiles}>
          <Images className="h-4 w-4" />{t("attachments.gallery", "Gallery")}
        </Picker>
      </div>
    )
  }
  return (
    <div className={className}>
      <Picker multiple disabled={disabled} onFiles={onFiles} title={t("attachments.add_hint", "Photos and videos — or drop them here")}>
        <Paperclip className="h-4 w-4" />{t("attachments.add", "Photo or video")}
      </Picker>
    </div>
  )
}

/** Files dropped on (or pasted into) a block: its wrapper props */
export function useFileDrop(onFiles: (files: FileList) => void, disabled?: boolean) {
  const [over, setOver] = React.useState(false)
  const props = {
    onDragOver: (e: React.DragEvent) => {
      if (disabled || !e.dataTransfer.types.includes("Files")) return
      e.preventDefault()
      setOver(true)
    },
    onDragLeave: () => setOver(false),
    onDrop: (e: React.DragEvent) => {
      setOver(false)
      if (disabled || !e.dataTransfer.files.length) return
      e.preventDefault()
      onFiles(e.dataTransfer.files)
    },
    onPaste: (e: React.ClipboardEvent) => {
      if (disabled || !e.clipboardData.files.length) return
      e.preventDefault()
      onFiles(e.clipboardData.files)
    },
  }
  return { over, props }
}
